import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import type { ProducerRole } from '@/types/team';

/**
 * Capability flags derived from role
 */
export interface ProducerCapabilities {
  /** Can access the producer portal at all */
  canViewPortal: boolean;
  /** Can edit producer info (grundinfo, plats, kontakt, kategorier) */
  canEditProducerInfo: boolean;
  /** Can manage media (bilder) */
  canManageMedia: boolean;
  /** Can manage publishing (publicering) */
  canManagePublishing: boolean;
  /** Can manage team (personer & roller) */
  canManageTeam: boolean;
  /** Can manage shop products */
  canManageShop: boolean;
  /** Can manage orders */
  canManageOrders: boolean;
  /** Can archive or delete producer (danger zone) - owner only */
  canManageDangerZone: boolean;
}

/**
 * Maps role to capability flags
 */
export function roleToCapabilities(role: ProducerRole | null): ProducerCapabilities {
  if (!role) {
    return {
      canViewPortal: false,
      canEditProducerInfo: false,
      canManageMedia: false,
      canManagePublishing: false,
      canManageTeam: false,
      canManageShop: false,
      canManageOrders: false,
      canManageDangerZone: false,
    };
  }

  switch (role) {
    case 'admin':
      return {
        canViewPortal: true,
        canEditProducerInfo: true,
        canManageMedia: true,
        canManagePublishing: true,
        canManageTeam: true,
        canManageShop: true,
        canManageOrders: true,
        canManageDangerZone: true,
      };
    case 'editor':
      return {
        canViewPortal: true,
        canEditProducerInfo: true,
        canManageMedia: true,
        canManagePublishing: false,
        canManageTeam: false,
        canManageShop: true,
        canManageOrders: false,
        canManageDangerZone: false,
      };
    case 'staff':
      return {
        canViewPortal: true,
        canEditProducerInfo: true,
        canManageMedia: true,
        canManagePublishing: false,
        canManageTeam: false,
        canManageShop: true,
        canManageOrders: true,
        canManageDangerZone: false,
      };
    case 'viewer':
      return {
        canViewPortal: true,
        canEditProducerInfo: false,
        canManageMedia: false,
        canManagePublishing: false,
        canManageTeam: false,
        canManageShop: false,
        canManageOrders: false,
        canManageDangerZone: false,
      };
    default:
      return {
        canViewPortal: false,
        canEditProducerInfo: false,
        canManageMedia: false,
        canManagePublishing: false,
        canManageTeam: false,
        canManageShop: false,
        canManageOrders: false,
        canManageDangerZone: false,
      };
  }
}

export type CapabilityKey = keyof ProducerCapabilities;

/**
 * Capability labels for UI messages (Swedish)
 */
export const CAPABILITY_LABELS: Record<CapabilityKey, string> = {
  canViewPortal: 'Visa portalen',
  canEditProducerInfo: 'Redigera gårdsinformation',
  canManageMedia: 'Hantera bilder',
  canManagePublishing: 'Hantera publicering',
  canManageTeam: 'Hantera team',
  canManageShop: 'Hantera produkter',
  canManageOrders: 'Hantera ordrar',
  canManageDangerZone: 'Arkivera eller radera gård',
};

/**
 * Role labels for error messages (Swedish)
 */
export const REQUIRED_ROLE_LABELS: Record<CapabilityKey, string> = {
  canViewPortal: 'medlem',
  canEditProducerInfo: 'personal eller högre',
  canManageMedia: 'personal eller högre',
  canManagePublishing: 'admin eller ägare',
  canManageTeam: 'admin eller ägare',
  canManageShop: 'personal eller högre',
  canManageOrders: 'personal eller högre',
  canManageDangerZone: 'ägare',
};

interface UseProducerAccessResult {
  role: ProducerRole | null;
  capabilities: ProducerCapabilities;
  isLoading: boolean;
  error: Error | null;
  /** Check if user has a specific capability */
  hasCapability: (capability: CapabilityKey) => boolean;
  /** True if role is viewer (read-only mode) */
  isReadOnly: boolean;
}

/**
 * Fetch user's role and derived capabilities for a producer
 */
export function useProducerAccess(producerId: string | undefined): UseProducerAccessResult {
  const { user, isAdmin } = useAuth();

  const { data: role, isLoading, error } = useQuery({
    queryKey: ['producer-access', producerId, user?.id],
    queryFn: async () => {
      if (!producerId || !user?.id) return null;

      // Admins get full access
      if (isAdmin) {
        return 'admin' as ProducerRole;
      }

      // Fetch membership role
      const { data, error } = await supabase
        .from('producer_members')
        .select('role')
        .eq('producer_id', producerId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Failed to fetch producer access:', error);
        throw error;
      }

      return (data?.role as ProducerRole) || null;
    },
    enabled: !!producerId && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const capabilities = roleToCapabilities(role ?? null);

  const hasCapability = (capability: CapabilityKey): boolean => {
    return capabilities[capability] ?? false;
  };

  return {
    role: role ?? null,
    capabilities,
    isLoading,
    error: error as Error | null,
    hasCapability,
    isReadOnly: role === 'viewer',
  };
}
